import type { IStorageService, UploadOptions } from '../../interfaces/src';
import { getSupabaseClient } from './client';

/**
 * Buckets that hold tenant-owned content. Writes go through a
 * `${tenant_id}/` path prefix so the per-bucket RLS policy
 * (see migration 20260426190000) can deny cross-tenant overwrites.
 *
 * Buckets NOT in this set are treated as global (eg legacy document
 * buckets where metadata stores the exact path); the prefix is not applied.
 */
const TENANT_SCOPED_BUCKETS = new Set(['erp-images', 'branding', 'storefront-products', 'storefront-assets']);

export class SupabaseStorageService implements IStorageService {
  private supabase;
  private ensuredBuckets = new Set<string>();
  // Resolved once per service instance via current_user_tenant_id().
  // Cached because (a) the user is bound to one tenant for the
  // lifetime of the session, and (b) prepending requires the value
  // synchronously inside upload/delete.
  private tenantIdPromise: Promise<string | null> | null = null;

  constructor(
    supabaseUrl: string,
    supabaseAnonKey: string,
    options?: import('@supabase/supabase-js').SupabaseClientOptions<'public'>,
  ) {
    this.supabase = getSupabaseClient(supabaseUrl, supabaseAnonKey, options);
  }

  private async ensureBucket(bucket: string): Promise<void> {
    if (this.ensuredBuckets.has(bucket)) return;
    const { error } = await this.supabase.storage.getBucket(bucket);
    if (error) {
      console.warn(`Bucket "${bucket}" not found — it should be created via a migration.`);
    }
    this.ensuredBuckets.add(bucket);
  }

  /**
   * Resolve the caller's tenant_id. Cached after the first call.
   * Returns null for anon callers (storefront) — they shouldn't be
   * uploading to tenant-scoped buckets at all; the RLS policy denies
   * them, and we deliberately don't fabricate a prefix to mask that.
   */
  private async getTenantId(): Promise<string | null> {
    if (!this.tenantIdPromise) {
      this.tenantIdPromise = (async () => {
        const { data, error } = await this.supabase.rpc('current_user_tenant_id');
        if (error || !data) return null;
        return typeof data === 'string' ? data : null;
      })();
    }
    return this.tenantIdPromise;
  }

  /**
   * Path resolver: for tenant-scoped buckets, ensure the path begins
   * with `${tenant_id}/`. We are tolerant of callers that already pass
   * a prefixed path (idempotent).
   */
  private async resolvePath(bucket: string, path: string): Promise<string> {
    if (!TENANT_SCOPED_BUCKETS.has(bucket)) return path;
    const tenantId = await this.getTenantId();
    if (!tenantId) {
      // Anonymous or out-of-tenant caller — pass through; RLS will
      // reject the write with a clear error rather than us guessing.
      return path;
    }
    if (path.startsWith(`${tenantId}/`)) return path;
    // Strip any leading slash to keep bucket paths clean
    const trimmed = path.replace(/^\/+/, '');
    return `${tenantId}/${trimmed}`;
  }

  async upload(
    bucket: string,
    path: string,
    file: File | Blob | ArrayBuffer,
    options?: UploadOptions,
  ): Promise<string> {
    await this.ensureBucket(bucket);
    const finalPath = await this.resolvePath(bucket, path);
    // supabase-js's upload() accepts File | Blob | ArrayBuffer | …
    // and sends it through fetch. We pass through unchanged. The
    // contentType resolves from the explicit `options.contentType`
    // first, then the Blob/File `type`, falling back to undefined
    // (Supabase will infer from the path's extension as a last resort).
    const inferredType =
      file instanceof ArrayBuffer ? undefined : file.type;
    const { error } = await this.supabase.storage
      .from(bucket)
      .upload(finalPath, file, {
        upsert: true,
        contentType: options?.contentType || inferredType || undefined,
      });
    if (error) throw error;
    return this.getPublicUrl(bucket, finalPath);
  }

  getPublicUrl(bucket: string, path: string): string {
    const { data } = this.supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  }

  async delete(bucket: string, path: string): Promise<void> {
    const finalPath = await this.resolvePath(bucket, path);
    const { error } = await this.supabase.storage.from(bucket).remove([finalPath]);
    if (error) throw error;
  }
}
